import { Tabs } from "./unassignedTabs";

/**
 * The lead counts shown in the list-detail header, derived from the list record and the active
 * view. Pure on purpose: the counts arrive from SQL aggregates and are **strings at runtime** even
 * though the schema types them as numbers, so every sum here coerces first (web CLAUDE.md §6) —
 * which is exactly the kind of thing that needs a test without mounting the page.
 */

/** The per-tab counts a list record carries. Values may be numeric strings from the API. */
export interface ListDetailCounts {
	draftCount?: number;
	savedForLaterCount?: number;
	assignedCount?: number;
	archivedCount?: number;
	underReviewCount?: number;
	trackedProfileCount?: number;
	/** Companies on a company-only list's Companies tab (PREC-4400). */
	companiesCount?: number;
}

/** Query params that narrow the visible rows; any of them means the header shows a filtered count. */
const VIEW_FILTER_PARAMS = [
	"filter",
	"searchTerm",
	"signalFilterStatus",
	"priorityFilterStatus",
	"inclusionStatus",
];

/** How many rows the given tab holds, or null when the list record has no count for it. */
export const tabCount = (counts: ListDetailCounts | undefined, tab: Tabs): number | null => {
	const byTab: Record<Tabs, number | undefined> = {
		[Tabs.UNASSIGNED]: counts?.draftCount,
		[Tabs.SAVED_FOR_LATER]: counts?.savedForLaterCount,
		[Tabs.ASSIGNED]: counts?.assignedCount,
		[Tabs.ARCHIVED]: counts?.archivedCount,
		[Tabs.UNDER_REVIEW]: counts?.underReviewCount,
		[Tabs.TRACKED_PROFILE]: counts?.trackedProfileCount,
		[Tabs.COMPANIES]: counts?.companiesCount,
	};

	return byTab[tab] ?? null;
};

/** Everything still in play — drafts plus saved-for-later — or null when the list is empty. */
export const totalListCount = (counts: ListDetailCounts | undefined): number | null => {
	const sum = +(counts?.draftCount ?? 0) + +(counts?.savedForLaterCount ?? 0);

	return sum > 0 ? sum : null;
};

/** Whether the current view narrows the rows, so the header should show the filtered total. */
export const hasActiveViewFilters = (searchParams: URLSearchParams): boolean =>
	VIEW_FILTER_PARAMS.some((param) => searchParams.has(param));

interface FilteredLeadCountInput {
	tab: Tabs;
	isFiltered: boolean;
	/** Row count the API reported for the current query. */
	itemCount?: number;
	/** Unfiltered count for the active tab. */
	tabCount: number | null;
}

/**
 * The count the header leads with. Tracked profiles are not leads, so that tab shows none at all;
 * otherwise a filtered view reports what the query returned and an unfiltered one the tab's total.
 */
export const filteredLeadCount = ({
	tab,
	isFiltered,
	itemCount,
	tabCount: countForTab,
}: FilteredLeadCountInput): number | null => {
	if (tab === Tabs.TRACKED_PROFILE || tab === Tabs.COMPANIES) {
		return null;
	}

	return isFiltered ? (itemCount ?? null) : countForTab;
};
